import { Box, Flex, Grid, HStack, Text } from "@chakra-ui/react";
import type { LucideIcon } from "lucide-react";
import { Bell, Heart, Menu, ShoppingCart, Wrench } from "lucide-react";
import { useLocation, useNavigate } from "react-router";
import { brandColors, radii } from "@core/theme";
import type { NavItem } from "@modules/registry";
import { navigationMenus } from "@modules/registry";
import { AMBER, NAVY } from "./accent";
import { HeaderSearch } from "./HeaderSearch";
import { LAYOUT_MAX, LAYOUT_PX } from "./layoutConstants";

/** Cart is in the header icon — avoid a duplicate tab in the strip. */
const MAIN_NAV = navigationMenus.filter((item) => item.id !== "cart");

type AppTopBarProps = {
  cartCount?: number;
  wishlistCount?: number;
  userName?: string | null;
  userInitial: string;
  onAccountClick: () => void;
  onWishlistClick: () => void;
  onNotificationsClick: () => void;
  notificationCount?: number;
  onCartClick: () => void;
  onMobileNavOpen: () => void;
};

const isNavActive = (pathname: string, url: string): boolean => {
  if (url === "/") return pathname === "/";
  return pathname === url || pathname.startsWith(`${url}/`);
};

const NavTab = ({
  item,
  active,
  onClick,
  compact,
}: {
  item: NavItem;
  active: boolean;
  onClick: () => void;
  compact?: boolean;
}) => {
  const Icon = item.icon;
  return (
    <Flex
      as="button"
      type="button"
      align="center"
      justify="center"
      gap={compact ? 0 : 2}
      flex={compact ? "0 0 auto" : 1}
      minW={compact ? "44px" : "max-content"}
      px={compact ? 2 : 3}
      py={2}
      borderRadius={`${radii.md}px`}
      fontSize="sm"
      fontWeight={active ? "700" : "500"}
      color={active ? brandColors.primary[700] : "rgba(255,255,255,0.85)"}
      bg={active ? "white" : "transparent"}
      transition="background 0.15s ease, color 0.15s ease"
      _hover={{
        bg: active ? "white" : "rgba(255,255,255,0.08)",
        color: active ? brandColors.primary[700] : "white",
      }}
      _focusVisible={{ outline: "none", boxShadow: `inset 0 0 0 2px ${AMBER[500]}` }}
      onClick={onClick}
      aria-label={item.title}
      title={compact ? item.title : undefined}
    >
      <Icon size={18} strokeWidth={active ? 2.5 : 2} />
      {compact ? null : <Box as="span">{item.title}</Box>}
    </Flex>
  );
};

const NavStrip = ({ pathname, onGo }: { pathname: string; onGo: (url: string) => void }) => (
  <Flex
    w="100%"
    gap={1}
    p={1}
    borderRadius={`${radii.lg}px`}
    bg="rgba(0,0,0,0.35)"
    borderWidth="1px"
    borderColor="rgba(255,255,255,0.06)"
  >
    {MAIN_NAV.map((item) => (
      <NavTab
        key={item.id}
        item={item}
        active={isNavActive(pathname, item.url)}
        onClick={() => onGo(item.url)}
      />
    ))}
  </Flex>
);

const IconBtn = ({
  icon: Icon,
  badge,
  label,
  onClick,
  variant = "ghost",
  size = "md",
}: {
  icon: LucideIcon;
  badge?: number;
  label: string;
  onClick: () => void;
  variant?: "ghost" | "solid";
  size?: "sm" | "md";
}) => {
  const box = size === "sm" ? "36px" : "40px";
  return (
    <Flex
      as="button"
      type="button"
      position="relative"
      align="center"
      justify="center"
      boxSize={box}
      borderRadius="full"
      aria-label={label}
      onClick={onClick}
      bg={variant === "solid" ? brandColors.primary[600] : "rgba(255,255,255,0.08)"}
      borderWidth="1px"
      borderColor={variant === "solid" ? brandColors.primary[500] : "rgba(255,255,255,0.1)"}
      _hover={{
        bg: variant === "solid" ? brandColors.primary[700] : "rgba(255,255,255,0.14)",
      }}
      flexShrink={0}
    >
      <Icon size={size === "sm" ? 17 : 19} color="white" />
      {badge !== undefined && badge > 0 ? (
        <Box
          position="absolute"
          top="0"
          right="0"
          minW="16px"
          h="16px"
          px="3px"
          display="flex"
          alignItems="center"
          justifyContent="center"
          borderRadius="full"
          bg={AMBER[300]}
          color={NAVY[900]}
          fontSize="9px"
          fontWeight="800"
          transform="translate(25%, -25%)"
        >
          {badge > 99 ? "99+" : badge}
        </Box>
      ) : null}
    </Flex>
  );
};

const BrandMark = ({ onClick }: { onClick: () => void }) => (
  <HStack gap={2} cursor="pointer" onClick={onClick} minW={0} role="group">
    <Flex
      align="center"
      justify="center"
      boxSize={{ base: "38px", md: "42px" }}
      borderRadius={`${radii.md}px`}
      bg={brandColors.primary[600]}
      flexShrink={0}
    >
      <Wrench size={20} color="#fff" />
    </Flex>
    <Box lineHeight="1.1">
      <Text fontWeight="800" fontSize={{ base: "md", md: "lg" }} color="white">
        Muthu
      </Text>
      <Text fontSize="9px" fontWeight="700" letterSpacing="0.15em" color={AMBER[300]}>
        CAR SPARES
      </Text>
    </Box>
  </HStack>
);

const ActionCluster = ({
  cartCount,
  wishlistCount,
  notificationCount,
  userInitial,
  userName,
  onWishlistClick,
  onNotificationsClick,
  onCartClick,
  onAccountClick,
  onMobileNavOpen,
}: Omit<AppTopBarProps, "onMobileNavOpen"> & { onMobileNavOpen: () => void }) => (
  <HStack gap={{ base: 1, md: 1.5 }} flexShrink={0}>
    <HStack gap={1} display={{ base: "none", lg: "flex" }}>
      <IconBtn icon={Heart} badge={wishlistCount} label="Wishlist" onClick={onWishlistClick} />
      <IconBtn
        icon={Bell}
        badge={notificationCount}
        label="Notifications"
        onClick={onNotificationsClick}
      />
    </HStack>
    <IconBtn
      icon={ShoppingCart}
      badge={cartCount}
      label="Cart"
      onClick={onCartClick}
      variant="solid"
      size="sm"
    />
    <Box display={{ base: "block", md: "none" }}>
      <IconBtn icon={Menu} label="Menu" onClick={onMobileNavOpen} />
    </Box>
    <Flex
      as="button"
      type="button"
      display="flex"
      align="center"
      gap={2}
      h={{ base: "36px", md: "40px" }}
      pl={1}
      pr={{ base: 1, sm: 3 }}
      borderRadius="full"
      borderWidth="1px"
      borderColor="rgba(255,255,255,0.15)"
      bg="rgba(255,255,255,0.06)"
      onClick={onAccountClick}
    >
      <Flex
        boxSize={{ base: "28px", md: "30px" }}
        borderRadius="full"
        bg={AMBER[500]}
        color={NAVY[900]}
        fontSize="xs"
        fontWeight="800"
        align="center"
        justify="center"
      >
        {userInitial}
      </Flex>
      <Text
        display={{ base: "none", md: "block" }}
        fontSize="sm"
        color="white"
        fontWeight="500"
        maxW={{ md: "80px", lg: "120px" }}
        isTruncated
      >
        {userName ?? "Account"}
      </Text>
    </Flex>
  </HStack>
);

export const AppTopBar = (props: AppTopBarProps) => {
  const navigate = useNavigate();
  const location = useLocation();
  const go = (url: string) => void navigate(url);

  return (
    <Box
      data-testid="app-site-header"
      bg={NAVY[900]}
      borderBottom={`2px solid ${brandColors.primary[500]}`}
    >
      <Box maxW={LAYOUT_MAX.header} mx="auto" px={LAYOUT_PX} py={{ base: 2, md: 3 }}>
        {/* ——— Mobile + tablet (< xl): stacked ——— */}
        <Box display={{ base: "block", xl: "none" }}>
          <Grid templateColumns="1fr auto" alignItems="center" gap={2} mb={{ base: 2, md: 3 }}>
            <BrandMark onClick={() => go("/")} />
            <ActionCluster {...props} />
          </Grid>
          <Box mb={{ base: 2, md: 3 }}>
            <HeaderSearch />
          </Box>
          {/* Mobile: hidden (hamburger menu); laptop lg–xl: icon + label */}
          <Box display={{ base: "none", lg: "block" }}>
            <NavStrip pathname={location.pathname} onGo={go} />
          </Box>
        </Box>

        {/* ——— Desktop wide (xl+): logo | search | actions, then full nav ——— */}
        <Box display={{ base: "none", xl: "block" }}>
          <Grid templateColumns="auto minmax(320px, 1fr) auto" alignItems="center" gap={4} mb={3}>
            <BrandMark onClick={() => go("/")} />
            <HeaderSearch />
            <ActionCluster {...props} />
          </Grid>
          <NavStrip pathname={location.pathname} onGo={go} />
        </Box>
      </Box>
    </Box>
  );
};
